import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { usePageTitle } from '@/hooks/usePageTitle';
import { allMovies, categories } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [filteredMovies, setFilteredMovies] = useState<Movie[]>([]);
  const [activeCat, setActiveCat] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  useEffect(() => {
    if (slug) {
      setActiveCat(slug);
      setFilteredMovies(allMovies.filter(m => m.categorySlug === slug));
    }
  }, [slug]);

  const categoryName = categories.find(c => c.slug === activeCat)?.name || '全部';
  usePageTitle(`${categoryName} - 糖心影视 | 高清影视免费在线观看`);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <nav className="px-4 md:px-6 py-3 bg-background-50 border-b border-background-200" aria-label="Breadcrumb">
          <ol className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-foreground-500">
            <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link></li>
            <li className="text-foreground-300">/</li>
            <li className="text-foreground-700 font-medium">{categoryName}</li>
          </ol>
        </nav>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-6">
              {categoryName}
            </h1>

            <div className="flex flex-wrap items-center gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeCat === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredMovies.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-film-line text-3xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {filteredMovies.map((movie, index) => (
                  <Link
                    key={movie.id}
                    to={`/article/${movie.id}`}
                    className="group bg-background-100 rounded-xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-200 cursor-pointer flex flex-col"
                  >
                    <div className="relative overflow-hidden aspect-video">
                      <img
                        src={movie.image}
                        alt={movie.title}
                        title={`${movie.title} - 糖心影视${categoryName}在线观看`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading={index > 2 ? 'lazy' : undefined}
                      />
                      <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-foreground-950/70 text-background-50 text-xs font-medium">
                        {movie.rating}
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                        <span className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium flex items-center gap-1.5">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-play-fill text-sm"></i>
                          </div>
                          立即播放
                        </span>
                      </div>
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                      <h2 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {movie.title}
                      </h2>
                      <p className="text-sm text-foreground-500 mt-1.5 line-clamp-2 leading-relaxed flex-1">
                        {movie.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        {movie.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200">
                        <span className="text-xs text-foreground-400">{movie.year}年</span>
                        <span className="text-xs text-foreground-400 flex items-center gap-1">
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-eye-line text-xs"></i>
                          </div>
                          {(movie.views / 10000).toFixed(0)}万次播放
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}